import * as SelectPrimitive from 'radix-ui/select';
import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { SettingsPopUpBody } from '../SettingsGroup/SettingsGroup';

/**
 * Single-choice select. Radix renders a real listbox with keyboard support and
 * type-ahead, which the native control gives us on desktop but not the styling
 * hooks for.
 */
export interface SelectOption {
  readonly value: string;
  readonly label: string;
  /** Rendered instead of the plain label (e.g. a coloured Badge). */
  readonly render?: ReactNode | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * A HEADING THIS OPTION SITS UNDER, and options carrying the same one in a row sit under
   * one heading (`phase-b-coach.md` D9's assignee control is the first list with two kinds
   * of answer in it: a named person, and a person the record points at).
   *
   * It is a property of the OPTION rather than a second array shape because every caller
   * that does not need headings must keep working unchanged, and because a grouped list is
   * still one ordered list - the heading marks where a run of options begins, it does not
   * reorder them. Radix renders it as a real `Select.Group`, so a screen reader announces
   * the heading with the option rather than reading a stray line of text.
   */
  readonly group?: string | undefined;
}

/**
 * THE TWO LOOKS A SELECT CAN HAVE, and there are exactly two (P7 round 2, F6).
 *
 * `field` is the bordered control this component has always drawn: a form field's width,
 * a border and a single down chevron. `popup` is macOS's POP-UP BUTTON in a settings row -
 * the chosen value as text followed by the kit's 24pt arrow-button badge, no border and no
 * width of its own. kit: `Apple macOS 27 UI Kit.sketch / Arrow Buttons/Light/Content Area/3 Rg/1 - Idle`, frame 24x24.
 * The Forms page's "Popup" row supplies the composition. One variant, added here rather than restyling every
 * menu in the product, because a settings row is the only place with the second look.
 */
export type SelectMenuVariant = 'field' | 'popup';

export interface SelectMenuProps {
  readonly value: string | undefined;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly invalid?: boolean | undefined;
  readonly id?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly variant?: SelectMenuVariant | undefined;
  /**
   * NO `className` (`ui-foundation.md` D1). A floating or chrome primitive owns its width,
   * its padding, its max-height and its collision behaviour; a caller that reaches into any
   * of them is the caller who eventually gets one of them wrong. Placement belongs to the
   * ROW the control sits in, so wrap it in a box and give the box the width.
   */
}

/**
 * Every floating menu surface: this select, the dropdown menu and the popover.
 * `chrome-raised` makes it a CHROME surface - which the tokens currently render as
 * glass - and `chrome-menu` gives it the shared open animation from ui/motion.css.
 *
 * IT NO LONGER CARRIES AN OVERFLOW RULE, and that half is `MENU_VIEWPORT`'s below:
 * `overflow-hidden` on the surface made the panel CLIP whatever did not fit, which is
 * the same dead end as letting it run off the screen. The two axes disagree now, so
 * each one is stated where it is decided.
 */
export const MENU_SURFACE =
  'chrome-raised chrome-menu rounded-surface border border-line shadow-popover layer-popover';

/**
 * The scroll behaviour a bounded panel needs, without the bound itself: the rounded
 * corner still has to clip sideways, and a menu that has reached its end must not hand
 * the rest of the gesture to the page behind it.
 */
const MENU_SCROLL = 'overflow-x-hidden overflow-y-auto overscroll-contain';

/**
 * A FLOATING PANEL NEVER RUNS OFF THE VIEWPORT (`polish` wave 1).
 *
 * Radix positions all three of these with one popper, and it already does two thirds of
 * the job: it FLIPS to the other side of the anchor and SHIFTS along it when the panel
 * would leave the screen, and it publishes what room is left as
 * `--radix-popper-available-height` on the wrapper it portals - read in the installed
 * `@radix-ui/react-popper@1.3.7`, which sets the property from the `size` middleware onto
 * `elements.floating`, the wrapper the content is a child of, so the value INHERITS down
 * to every class here.
 *
 * WHAT IT DOES NOT DO IS APPLY THAT NUMBER. Nothing in Radix bounds the panel's height,
 * so a menu with more rows than the screen has room for simply extended past the bottom
 * edge with no way to reach the rest - which is what the sort menu did on a laptop
 * (`CRM-POLISH-AUDIT-2026-08-18.md`). Bounding it is one declaration and the scroll is
 * the other; neither is any use alone.
 *
 * It is a CONSTANT rather than a rule inside each primitive because the three panels are
 * one mechanism: a fourth floating surface must inherit this by composing the pair, not
 * by remembering to repeat two utilities.
 */
export const MENU_VIEWPORT = `max-h-(--radix-popper-available-height) ${MENU_SCROLL}`;

/**
 * The select keeps its own 18rem design cap - a listbox is read, not scrolled through -
 * and takes whichever of the two is smaller. One `max-height` declaration, because two
 * `max-h-*` utilities on one element would be settled by the order Tailwind emits them
 * in rather than by the order they are written.
 */
const SELECT_VIEWPORT = `max-h-[min(18rem,var(--radix-popper-available-height))] ${MENU_SCROLL}`;

/**
 * THE LOOK OF A CONTROL THAT OPENS A PANEL, exported for the one other control that is
 * exactly that and is not a select: the filter row's VALUE, which opens its editor as a
 * nested layer (`ui-foundation.md` D11). Two hand-matched button styles in one row is how
 * a row starts looking like two different products.
 */
export const CONTROL_TRIGGER =
  'flex min-h-(--touch-target-min) w-full items-center justify-between gap-2 rounded-control border bg-surface ' +
  'px-3 py-2 text-form md:text-sm text-ink transition-colors ' +
  'disabled:bg-surface-sunken disabled:opacity-70 ' +
  'border-line hover:border-line-strong';

/**
 * Consecutive options that name the same heading, as one run each.
 *
 * By POSITION rather than by collecting every option with the same name: a list is what the
 * caller wrote, and re-sorting it here would move an option the caller put where it is.
 */
function runsOf(
  options: readonly SelectOption[],
): readonly { readonly group: string | undefined; readonly options: readonly SelectOption[] }[] {
  const runs: { group: string | undefined; options: SelectOption[] }[] = [];
  for (const option of options) {
    const last = runs[runs.length - 1];
    if (last !== undefined && last.group === option.group) last.options.push(option);
    else runs.push({ group: option.group, options: [option] });
  }
  return runs;
}

function renderOption(option: SelectOption) {
  return (
    <SelectPrimitive.Item
      key={option.value}
      value={option.value}
      {...(option.disabled !== undefined ? { disabled: option.disabled } : {})}
      className={cx(
        'flex min-h-(--touch-target-min) items-center gap-2 rounded-control px-2.5 text-sm text-ink',
        'outline-none data-highlighted:bg-surface-hover',
        'data-disabled:opacity-50',
      )}
    >
      <span className="flex w-4 shrink-0 items-center justify-center text-accent">
        <SelectPrimitive.ItemIndicator>
          <Icon name="check" size="0.875rem" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{option.render ?? option.label}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectMenu({
  value,
  onValueChange,
  options,
  placeholder = 'Select',
  disabled = false,
  invalid = false,
  id,
  ariaLabel,
  variant = 'field',
}: SelectMenuProps) {
  // `exactOptionalPropertyTypes` is on and Radix declares these as `value?:
  // string` rather than `string | undefined`, so an explicit `undefined` is not
  // the same as an absent prop. Conditional spreads keep both honest.
  return (
    <SelectPrimitive.Root
      {...(value !== undefined ? { value } : {})}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        className={cx(
          variant === 'popup' ? 'settings-popup' : CONTROL_TRIGGER,
          'data-placeholder:text-ink-subtle',
          invalid && variant !== 'popup' && 'border-danger',
        )}
      >
        {variant === 'popup' ? (
          <SettingsPopUpBody value={<SelectPrimitive.Value placeholder={placeholder} />} />
        ) : (
          <>
            <SelectPrimitive.Value placeholder={placeholder} />
            <SelectPrimitive.Icon className="text-ink-subtle">
              <Icon name="chevron-down" />
            </SelectPrimitive.Icon>
          </>
        )}
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        {/* The surface clips (its corners are rounded); the VIEWPORT inside it is the
            scroll region, which is Radix's own division of labour here. */}
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cx(MENU_SURFACE, 'overflow-hidden')}
        >
          <SelectPrimitive.Viewport className={cx(SELECT_VIEWPORT, 'p-1')}>
            {runsOf(options).map((run) =>
              run.group === undefined ? (
                run.options.map(renderOption)
              ) : (
                <SelectPrimitive.Group key={`group:${run.group}`}>
                  {/* The `DropdownLabel` treatment, because the two panels are one material. */}
                  <SelectPrimitive.Label className="px-2.5 py-2 text-2xs font-semibold tracking-widest text-ink-subtle uppercase">
                    {run.group}
                  </SelectPrimitive.Label>
                  {run.options.map(renderOption)}
                </SelectPrimitive.Group>
              ),
            )}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
